import { useEffect, useRef, useState } from 'react';
import { mealsService, Meal, MealDayTotal } from '../../api/services/MealsService';
import { userGoalsService, UserGoal } from '../../api/services/UserGoalsService';
import { getLocalYyyyMmDd, formatHumanFromYyyyMmDd } from '../../utils/dateUtils';
import { WeightCalculatorResult } from '../weight/WeightCalculator';
import i18next from 'i18next';

export const useFoodData = (activeTab: 'info' | 'track', isPublicMode = false) => {
  const [savedGoal, setSavedGoal] = useState<UserGoal | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showAddMealPanel, setShowAddMealPanel] = useState(false);
  const [mealText, setMealText] = useState('');
  const [isAnalyzingMeal, setIsAnalyzingMeal] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisFeedback, setAnalysisFeedback] = useState<string | null>(null);
  const [analyzedName, setAnalyzedName] = useState('');
  const [analyzedCalories, setAnalyzedCalories] = useState<number>(0);
  const [analyzedProtein, setAnalyzedProtein] = useState<number>(0);
  const [analyzedCarbs, setAnalyzedCarbs] = useState<number>(0);
  const [analyzedFats, setAnalyzedFats] = useState<number>(0);

  const [selectedMealDate, setSelectedMealDate] = useState<string>(() => getLocalYyyyMmDd());

  const [meals, setMeals] = useState<Meal[]>([]);
  const [mealHistory, setMealHistory] = useState<MealDayTotal[]>([]);

  const [showHistoryDayPanel, setShowHistoryDayPanel] = useState(false);
  const [historyDayDate, setHistoryDayDate] = useState<string>('');
  const [historyDayTitle, setHistoryDayTitle] = useState<string>('');
  const [historyDayMeals, setHistoryDayMeals] = useState<Meal[]>([]);
  const [isLoadingHistoryDayMeals, setIsLoadingHistoryDayMeals] = useState(false);

  const historyMealsRequestIdRef = useRef(0);

  useEffect(() => {
    if (isPublicMode) {
      setLoading(false);
      setSavedGoal(null);
      return;
    }

    const loadGoal = async () => {
      try {
        const goal = await userGoalsService.getGoal();
        setSavedGoal(goal);
      } catch (error) {
        console.error('Failed to load user goal:', error);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'track') {
      void loadGoal();
    }
  }, [activeTab, isPublicMode]);

  useEffect(() => {
    const loadMeals = async () => {
      if (activeTab !== 'track') return;
      if (isPublicMode) return;
      if (!savedGoal) return;

      const today = getLocalYyyyMmDd();
      try {
        const data = await mealsService.getMeals(today);
        setMeals(data);
      } catch (error) {
        console.error('Failed to load meals:', error);
      }
    };

    void loadMeals();
  }, [activeTab, savedGoal, isPublicMode]);

  useEffect(() => {
    const loadHistory = async () => {
      if (activeTab !== 'track') return;
      if (isPublicMode) return;
      if (!savedGoal) return;
      try {
        const data = await mealsService.getMealHistory(7);
        setMealHistory(data);
      } catch (error) {
        console.error('Failed to load meal history:', error);
      }
    };

    void loadHistory();
  }, [activeTab, savedGoal, isPublicMode]);

  const handleSaveGoal = async (
    result: WeightCalculatorResult,
    inputs: {
      targetWeight: number;
      currentWeight: number;
      height: number;
      age: number;
      gender: 'male' | 'female';
      timeframeMonths: number;
      activityLevel: number;
    }
  ) => {
    try {
      const nextGoal = await userGoalsService.saveGoal(result, inputs);
      setSavedGoal(nextGoal);
      setShowCalculator(false);
    } catch (error) {
      console.error('Failed to save goal:', error);
    }
  };

  const handleAnalyzeMeal = async () => {
    setIsAnalyzingMeal(true);
    setAnalysisError(null);
    setAnalysisFeedback(null);
    try {
      const res = await mealsService.analyzeMeal(mealText);
      setAnalyzedName(res.name);
      setAnalyzedCalories(res.calories);
      setAnalyzedProtein(res.protein);
      setAnalyzedCarbs(res.carbs);
      setAnalyzedFats(res.fats);
      setAnalysisFeedback(res.feedback || null);
    } catch (e: any) {
      setAnalysisError(e?.message || i18next.t('food.addMeal.analyzeFailed'));
    } finally {
      setIsAnalyzingMeal(false);
    }
  };

  const refreshHistory = async () => {
    try {
      const data = await mealsService.getMealHistory(7);
      setMealHistory(data);
    } catch (error) {
      console.error('Failed to refresh meal history:', error);
    }
  };

  const handleSaveMeal = async () => {
    if (!savedGoal) return;

    try {
      const created = await mealsService.saveMeal({
        mealDate: selectedMealDate,
        name: analyzedName.trim() || mealText.trim().slice(0, 60) || i18next.t('food.addMeal.mealFallbackName'),
        label: formatHumanFromYyyyMmDd(selectedMealDate),
        calories: Math.max(0, Math.round(analyzedCalories || 0)),
        protein: Math.max(0, Math.round(analyzedProtein || 0)),
        carbs: Math.max(0, Math.round(analyzedCarbs || 0)),
        fats: Math.max(0, Math.round(analyzedFats || 0)),
      });

      const today = getLocalYyyyMmDd();
      if (selectedMealDate === today) {
        setMeals((prev) => [created, ...prev]);
      }

      await refreshHistory();

      setShowAddMealPanel(false);
      setMealText('');
      setAnalysisError(null);
      setAnalysisFeedback(null);
      setAnalyzedName('');
      setAnalyzedCalories(0);
      setAnalyzedProtein(0);
      setAnalyzedCarbs(0);
      setAnalyzedFats(0);
    } catch (error) {
      console.error('Failed to save meal:', error);
      setAnalysisError(i18next.t('food.addMeal.saveFailed'));
    }
  };

  const openHistoryDay = async (date: string, title: string) => {
    const dateKey = String(date).slice(0, 10);
    const requestId = ++historyMealsRequestIdRef.current;

    setShowHistoryDayPanel(true);
    setHistoryDayDate(dateKey);
    setHistoryDayTitle(title);
    setHistoryDayMeals([]);
    setIsLoadingHistoryDayMeals(true);

    try {
      const data = await mealsService.getMeals(dateKey);
      if (requestId === historyMealsRequestIdRef.current) {
        setHistoryDayMeals(data);
      }
    } catch (error) {
      console.error('Failed to load meals for day:', dateKey, error);
      if (requestId === historyMealsRequestIdRef.current) {
        setHistoryDayMeals([]);
      }
    } finally {
      if (requestId === historyMealsRequestIdRef.current) {
        setIsLoadingHistoryDayMeals(false);
      }
    }
  };

  return {
    savedGoal,
    setSavedGoal,
    showCalculator,
    setShowCalculator,
    loading,

    showAddMealPanel,
    setShowAddMealPanel,
    mealText,
    setMealText,
    isAnalyzingMeal,
    analysisError,
    analysisFeedback,
    setAnalysisError,
    setAnalysisFeedback,
    analyzedCalories,
    analyzedProtein,
    analyzedCarbs,
    analyzedFats,
    setAnalyzedCalories,
    setAnalyzedProtein,
    setAnalyzedCarbs,
    setAnalyzedFats,

    selectedMealDate,
    setSelectedMealDate,

    meals,
    mealHistory,

    showHistoryDayPanel,
    setShowHistoryDayPanel,
    historyDayDate,
    historyDayTitle,
    historyDayMeals,
    isLoadingHistoryDayMeals,

    handleSaveGoal,
    handleAnalyzeMeal,
    handleSaveMeal,
    openHistoryDay,

    refreshHistory,
  };
};
